import { EventType, PublicClientApplication, type AuthenticationResult } from '@azure/msal-browser';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { configureApiAuth } from './auth/apiAuth';
import { msalConfig } from './auth/msalConfig';
import { setRedirectError } from './auth/redirectError';
import i18n from './i18n';

function renderStartupFailure(): void {
  const container = document.getElementById('root');
  if (!container) return;

  const alert = document.createElement('div');
  alert.setAttribute('role', 'alert');
  alert.textContent = i18n.t('errors.startupFailed');
  container.replaceChildren(alert);
}

async function bootstrap(): Promise<void> {
  try {
    const instance = new PublicClientApplication(msalConfig);

    // MSAL v3+ must be initialized, and the redirect answer must be consumed, before anything renders —
    // otherwise the first token request races the hash still sitting in the URL.
    await instance.initialize();

    try {
      await instance.handleRedirectPromise();
    } catch (error) {
      // A refused sign-in comes back as #error=… in the fragment, which rejects here. It must not
      // escape: an exception at this point aborts bootstrap before the first render, so the user gets
      // a blank page and the reason survives only in the console. Keep it and show it instead.
      setRedirectError(error);
    }

    const accounts = instance.getAllAccounts();
    if (!instance.getActiveAccount() && accounts[0]) instance.setActiveAccount(accounts[0]);

    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        if (payload.account) instance.setActiveAccount(payload.account);
      }
    });

    configureApiAuth(instance);

    const container = document.getElementById('root');
    if (!container) throw new Error('Root container is missing from index.html.');

    createRoot(container).render(
      <StrictMode>
        <App instance={instance} />
      </StrictMode>,
    );
  } catch {
    renderStartupFailure();
  }
}

void bootstrap().catch(renderStartupFailure);
